import { sdk } from "@gc/ipecs-web-sdk";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { getPresences } from "../api/presences";
import { useAuth } from "./AuthContext";
import { useMyPresence } from "./MyPresenceContext";

// SDK events can be missed (e.g. a burst on the hour), so re-fetch everyone's
// presence regularly as well as applying events
export const PRESENCE_REFRESH_MS = 60000;

// The API caches presences for 10s, so a poll can be older than a recent SDK
// event; polls leave users with an event this recent alone
const EVENT_TRUST_MS = 15000;

// SDK ids are prefixed (wuc_1_<uuid>), ids from our API are the bare uuid
const toApiUserId = (sdkUserId) => sdkUserId.replace(/^wuc_\d+_/, "");

const samePresence = (a, b) =>
  a.presence === b.presence && a.statusMessage === b.statusMessage;

const PresenceContext = React.createContext();
const PresenceProvider = ({ children }) => {
  const { presence } = sdk;
  const [presences, setPresences] = useState([]);
  const { ipecsUser } = useAuth();
  const { setPresence } = useMyPresence();
  const myId = ipecsUser.myInfo.id;
  const lastEventAt = useRef(new Map());
  const fetching = useRef(false);

  const handleAllPresences = useCallback(() => {
    if (fetching.current) return;
    fetching.current = true;

    getPresences()
      .then((res) => {
        // the API returns [] when iPECS fails; keep what we have
        if (res.data.length === 0) return;

        const recentSince = Date.now() - EVENT_TRUST_MS;

        setPresences((prev) => {
          let changed = prev.length !== res.data.length;

          const next = res.data.map((x) => {
            const current = prev.find((p) => p.userId === x.user_id);

            if (current && lastEventAt.current.get(x.user_id) > recentSince) {
              return current;
            }

            const polled = {
              userId: x.user_id,
              presence: x.status,
              statusMessage: x.status_message,
            };

            if (current && samePresence(current, polled)) return current;

            changed = true;
            return polled;
          });

          return changed ? next : prev;
        });
      })
      .catch((e) => {})
      .finally(() => {
        fetching.current = false;
      });
  }, []);

  useEffect(() => {
    handleAllPresences();

    const refreshIfVisible = () => {
      if (document.visibilityState === "visible") handleAllPresences();
    };

    const interval = setInterval(refreshIfVisible, PRESENCE_REFRESH_MS);
    document.addEventListener("visibilitychange", refreshIfVisible);
    window.addEventListener("focus", refreshIfVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", refreshIfVisible);
      window.removeEventListener("focus", refreshIfVisible);
    };
  }, [handleAllPresences]);

  useEffect(() => {
    // Apply each batch as it arrives; batches arriving between renders must not
    // replace one another
    presence.onChangePresence((changedPresences) => {
      const mine = changedPresences.filter((e) => e.userId === myId);
      const others = changedPresences.filter((e) => e.userId !== myId);

      if (mine.length > 0) setPresence(mine[mine.length - 1].presence);
      if (others.length === 0) return;

      const now = Date.now();
      others.forEach((e) =>
        lastEventAt.current.set(toApiUserId(e.userId), now),
      );

      setPresences((prev) => {
        const next = [...prev];

        others.forEach((e) => {
          const userId = toApiUserId(e.userId);
          const index = next.findIndex((obj) => obj.userId === userId);
          const update = {
            ...(e.presence !== undefined && { presence: e.presence }),
            ...(e.statusMessage !== undefined && {
              statusMessage: e.statusMessage,
            }),
          };

          if (index === -1) {
            next.push({ userId, ...update });
          } else {
            next[index] = { ...next[index], ...update };
          }
        });

        return next;
      });
    });

    return () => presence.onChangePresence(null);
  }, [presence, myId, setPresence]);

  // Changes identity when presences change so indicators re-read their user
  const getPresenceByUserId = useCallback(
    async (userId) => {
      const existing = presences.find((obj) => obj.userId === userId);

      if (existing) {
        return existing;
      }

      const placeholder = { userId, presence: "unknown", statusMessage: "" };
      setPresences((prev) =>
        prev.some((obj) => obj.userId === userId)
          ? prev
          : [...prev, placeholder],
      );
      return placeholder;
    },
    [presences],
  );

  const defaultContext = { getPresenceByUserId };
  return (
    <PresenceContext.Provider value={defaultContext}>
      {children}
    </PresenceContext.Provider>
  );
};

function usePresence() {
  return useContext(PresenceContext);
}

export { PresenceProvider, usePresence };
